import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import {
  useGetBroadcast,
  useGetBroadcasts,
  useGetMyAccount,
  useGetMyProfile,
  useGetMySongs,
} from "@/api/queries";
import useAppStore from "@/store/appStateZustand";

import type { BroadcastFormValues } from "../utils/broadcastSchema";

import { createBroadcastFormSchema } from "../utils/broadcastSchema";

export default function useBroadcastForm() {
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;

  const { broadcastId } = useParams({
    strict: false,
    select: (p): { broadcastId: string | undefined } => ({
      broadcastId: (p as Record<string, string | undefined>).broadcastId,
    }),
  });

  const isUpdate = Boolean(broadcastId);

  // Queries
  const myAccount = useGetMyAccount({ artistaccountcode: selectedAccount });
  const profile = useGetMyProfile();
  const rawOriginatorCode = myAccount.data?.originatorcode?.code;
  const originatorCode = rawOriginatorCode != null && rawOriginatorCode !== ""
    ? rawOriginatorCode
    : undefined;
  const broadcasts = useGetBroadcasts({
    ac: selectedAccount,
    oc: originatorCode,
  });
  const broadcast = useGetBroadcast({
    ac: selectedAccount,
    id: broadcastId ?? "",
  });
  const songs = useGetMySongs({ aac: selectedAccount });

  const { refetch: refetchBroadcast } = broadcast;

  const schemaRef = useRef(createBroadcastFormSchema());

  const form = useForm<BroadcastFormValues>({
    resolver: async (values, context, options) =>
      zodResolver(schemaRef.current)(values, context, options),
  });

  // refetch in edit mode
  useEffect(() => {
    if (selectedAccount && isUpdate) {
      void refetchBroadcast();
    }
  }, [selectedAccount, refetchBroadcast, isUpdate]);

  useEffect(() => {
    if (!isUpdate || !broadcast.data?.[0])
      return;

    const raw = broadcast.data[0];

    const dateNotValid = Number.isNaN(Date.parse(raw.date_aired));
    const dateAired = dateNotValid
      ? undefined
      : new Date(Date.parse(raw.date_aired)).toISOString().slice(0, 10);

    let songWorkcode: string | undefined;
    if (raw.song != null || raw.song !== undefined) {
      const parsed = JSON.parse(raw.song) as Record<string, unknown>;
      songWorkcode
        = typeof parsed.workcode === "string" ? parsed.workcode
          : typeof parsed.tc === "string" ? parsed.tc
            : undefined;
    }

    const values: Partial<BroadcastFormValues> = {
      song: songWorkcode,
      medium: (raw.medium as BroadcastFormValues["medium"]) ?? undefined,
      station: raw.station ?? undefined,
      program: raw.program ?? undefined,
      country: raw.country ?? undefined,
      dateAired,
      evidence: raw.evidence ?? undefined,
    };

    form.reset(values);
  }, [broadcast.data, isUpdate, form]);

  return {
    form,
    isUpdate,
    broadcastId,
    myAccount,
    profile,
    broadcasts,
    broadcast,
    refetchBroadcast,
    songs,
  };
}
